// Profile popover. Triggered from a button in the top bar (and
// reused by the mobile menu trigger). Renders user identity +
// navigation + sign-out as a popover anchored to the trigger.

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Building2, ChevronRight, LogOut, Search, Settings, UserCog, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type ProfileMenuEntry = {
  label: string;
  hint?: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
};

export function ProfileMenu({
  trigger,
  displayName,
  email,
  initials,
  avatarUrl,
  avatarColor,
  entries,
  onSignOut,
  align = "end",
  side = "bottom",
}: {
  trigger: React.ReactNode;
  displayName: string | null;
  email: string | null;
  initials: string;
  /** Public Storage URL of the user's chosen profile picture. When
   *  set the popover header renders the image instead of the
   *  initials — falls back to initials seamlessly when null (e.g.
   *  signed-up user who has never uploaded an avatar). */
  avatarUrl?: string | null;
  avatarColor: string;
  entries: ProfileMenuEntry[];
  onSignOut: () => void;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={8}
        className="w-72 overflow-hidden rounded-2xl border border-border/60 p-0 shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-border/60 bg-secondary/30 p-3">
          <span
            aria-hidden
            className={cn(
              "grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br text-sm font-semibold text-white",
              avatarColor,
            )}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="h-full w-full object-cover"
                key={avatarUrl}
              />
            ) : (
              initials
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{displayName ?? "—"}</div>
            <div className="truncate text-xs text-muted-foreground">{email ?? "—"}</div>
          </div>
        </div>
        <ul className="max-h-[60vh] space-y-0.5 overflow-y-auto p-1.5">
          {entries.map(({ label, hint, icon: Icon, onSelect, danger }) => (
            <li key={label}>
              <button
                type="button"
                onClick={onSelect}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors",
                  danger
                    ? "text-destructive hover:bg-destructive/10"
                    : "text-foreground/85 hover:bg-secondary",
                )}
              >
                <Icon className="h-4 w-4 text-foreground/60" aria-hidden />
                <span className="flex-1 font-medium">{label}</span>
                {hint && (
                  <span className="text-[11px] text-muted-foreground">{hint}</span>
                )}
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <div className="border-t border-border/60 p-1.5">
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            <span className="flex-1 font-medium">登出</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Default entries the shell uses when composing the menu. The
// family-admin "管理家庭成員" action moved to a dedicated top-bar
// icon button on the wide viewport (AppShell.tsx); it is not part of
// this menu anymore.
export function profileEntriesFor({
  isOrgAdmin,
  onOpenSettings,
  onOpenSearch,
  onOpenTree,
  onGotoOrg,
}: {
  isOrgAdmin: boolean;
  onOpenSettings: () => void;
  onOpenSearch: () => void;
  onOpenTree: () => void;
  onGotoOrg: () => void;
}): ProfileMenuEntry[] {
  return [
    { label: "搜尋", hint: "⌘K", icon: Search, onSelect: onOpenSearch },
    { label: "我的 FamTree", icon: UserCog, onSelect: onOpenTree },
    { label: "設定", icon: Settings, onSelect: onOpenSettings },
    ...(isOrgAdmin
      ? [{ label: "機構後台", hint: "管理本機構", icon: Building2, onSelect: onGotoOrg }]
      : []),
  ];
}
